
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, XCircle, UserPlus } from "lucide-react";

interface InvitationAcceptanceProps {
  token: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export function InvitationAcceptance({ token, onSuccess, onCancel }: InvitationAcceptanceProps) {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [invitationDetails, setInvitationDetails] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchInvitationDetails = async () => {
      try {
        // Try to fetch staff invitation first
        const { data: staffInvitation, error: staffError } = await supabase
          .from('staff_invitations')
          .select('*, clinics(name)')
          .eq('invitation_token', token)
          .eq('status', 'pending')
          .gt('expires_at', new Date().toISOString())
          .single();

        if (staffInvitation) {
          setInvitationDetails({ ...staffInvitation, type: 'staff' });
          return;
        }

        // If not staff, try patient invitation
        const { data: patientInvitation, error: patientError } = await supabase
          .from('patient_invitations')
          .select('*, clinics(name)')
          .eq('invitation_token', token)
          .eq('status', 'pending')
          .gt('expires_at', new Date().toISOString())
          .single();

        if (patientInvitation) {
          setInvitationDetails({ ...patientInvitation, type: 'patient' });
          return;
        }

        setError('Invalid or expired invitation token');
      } catch (error) {
        console.error('Error fetching invitation:', error);
        setError('Failed to load invitation details');
      }
    };

    fetchInvitationDetails();
  }, [token]);

  const handleAcceptInvitation = async () => {
    if (!user || !invitationDetails) return;

    setIsLoading(true);
    try {
      let result;
      
      if (invitationDetails.type === 'staff') {
        const { data, error } = await supabase.rpc('accept_staff_invitation', {
          p_invitation_token: token,
          p_user_id: user.id
        });
        result = { data, error };
      } else {
        const { data, error } = await supabase.rpc('accept_patient_invitation', {
          p_invitation_token: token,
          p_user_id: user.id
        });
        result = { data, error };
      }

      if (result.error) throw result.error;

      if (result.data) {
        toast({
          title: "Invitation Accepted",
          description: `You've successfully joined ${invitationDetails.clinics?.name || 'the clinic'}!`,
        });
        onSuccess();
      } else {
        throw new Error('Failed to accept invitation');
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      toast({
        title: "Error",
        description: "Failed to accept invitation. It may be expired or invalid.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-2" />
            <CardTitle>Invalid Invitation</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={onCancel} variant="outline" className="w-full">
              Continue to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!invitationDetails) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p>Loading invitation details...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <UserPlus className="w-12 h-12 text-blue-600 mx-auto mb-2" />
          <CardTitle>Clinic Invitation</CardTitle>
          <CardDescription>
            You've been invited to join {invitationDetails.clinics?.name || 'a clinic'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">Invitation Details</h4>
            <div className="space-y-1 text-sm text-blue-800">
              <p><strong>Clinic:</strong> {invitationDetails.clinics?.name}</p>
              <p><strong>Role:</strong> {invitationDetails.type === 'staff' ? invitationDetails.role : 'Patient'}</p>
              <p><strong>Invited by:</strong> {invitationDetails.email}</p>
              {invitationDetails.notes && (
                <p><strong>Note:</strong> {invitationDetails.notes}</p>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            <Button 
              onClick={handleAcceptInvitation} 
              disabled={isLoading}
              className="flex-1"
            >
              {isLoading ? "Accepting..." : "Accept Invitation"}
            </Button>
            <Button variant="outline" onClick={onCancel}>
              Decline
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
